'use client';

import { useState, useRef, useEffect } from 'react';

import { Dropdown } from '@/components/atoms';
import { TeamLeaguePlayerInfo } from '@/entities';

interface PlayerLockedViewProps {
	selectedPlayer: TeamLeaguePlayerInfo | undefined;
	selectedPlayerRawName?: string | null;
	swapperOutPlayer?: string | null;
}

interface PlayerSelectProps {
	players: TeamLeaguePlayerInfo[];
	onSelect: (selectedPlayerId: string | number) => void;
	selectedPlayer?: TeamLeaguePlayerInfo;
	selectedPlayerRawName?: string | null;
	isLocked?: boolean;
	isBlured?: boolean;
	playerLabel: string;
	placeholder: string;
	swapperOutPlayer?: string | null;
	isPlayerDisabledFn?: (player: TeamLeaguePlayerInfo) => boolean;
	allowEmptySelection?: boolean;
}

export const PlayerSelect = ({
	players,
	onSelect,
	selectedPlayer: initialSelectedPlayer,
	selectedPlayerRawName,
	isLocked = false,
	isBlured = false,
	playerLabel,
	placeholder,
	swapperOutPlayer,
	isPlayerDisabledFn,
	allowEmptySelection
}: PlayerSelectProps) => {
	const [selectedPlayer, setSelectedPlayer] = useState<TeamLeaguePlayerInfo | undefined>(initialSelectedPlayer);
	const [dropdownWidth, setDropdownWidth] = useState<number>(0);
	const dropdownContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const updateWidth = () => {
			if (dropdownContainerRef.current) {
				setDropdownWidth(dropdownContainerRef.current.offsetWidth);
			}
		};

		// Initial width calculation
		updateWidth();

		// Update on resize
		window.addEventListener('resize', updateWidth);
		return () => window.removeEventListener('resize', updateWidth);
	}, []);

	const formattedPlayers = [
		...(allowEmptySelection ? [{ value: '', label: 'None', isDisabled: false }] : []),
		...players.map((player) => ({
			value: player.userUuid,
			label: player.fullName,
			isDisabled: isPlayerDisabledFn?.(player) || false
		}))
	];

	const handleSelect = (selectedPlayerId: string) => {
		const newSelectedPlayer = players.find((player) => player.userUuid === selectedPlayerId);
		setSelectedPlayer(newSelectedPlayer);
		onSelect(selectedPlayerId);
	};

	return (
		<div className="flex-1">
			{!isLocked ? (
				<>
					<p className="mb-2 hidden text-base font-medium text-gray-700 sm:block">{playerLabel}</p>
					<div ref={dropdownContainerRef}>
						<Dropdown
							options={formattedPlayers}
							value={selectedPlayer?.userUuid || ''}
							placeholder={placeholder}
							onSelect={handleSelect}
							showAll={false}
							menuListStyle={{ minWidth: `${dropdownWidth}px` }}
							className="hover:bg-gray-50"
						/>
					</div>
				</>
			) : (
				<>
					{isBlured ? (
						<div>
							<p className="mb-2 hidden text-base font-medium text-gray-700 sm:block">{playerLabel}</p>
							<div className="relative w-full">
								<div className="rounded-md border border-gray-300 px-4 py-2 h-10 flex items-center bg-white">
									<p className="text-gray-500 text-sm">Player locked</p>
								</div>
								<div className="absolute inset-0 z-7 rounded-md border border-gray-300 backdrop-blur-sm bg-white/60 pointer-events-none" />
							</div>
						</div>
					) : (
						<PlayerLockedView
							selectedPlayer={swapperOutPlayer ? initialSelectedPlayer : selectedPlayer}
							selectedPlayerRawName={selectedPlayerRawName}
							swapperOutPlayer={swapperOutPlayer}
						/>
					)}
				</>
			)}
		</div>
	);
};

const PlayerLockedView = ({ selectedPlayer, selectedPlayerRawName, swapperOutPlayer }: PlayerLockedViewProps) => (
	<>
		<p className="text-base font-medium text-gray-900 text-center">{selectedPlayer?.fullName || selectedPlayerRawName || 'TBD'}</p>
		{swapperOutPlayer && (
			<div className="flex flex-col">
				<p className="text-sm font-medium text-gray-600 text-center"> {swapperOutPlayer} (Sub)</p>
			</div>
		)}
	</>
);
